import { apiUrl, currentUserId, currentUserQuery } from "./auth";
import { markTaskNoteDone } from "./coffre";
import { kanbanOn, setKanbanOn } from "./kanbanLink";
import { saveSuivi } from "./suivi";

export type WeekTask = {
  id: string;
  title: string;
  description: string;
  status: string;
  start: string;
  client_name: string;
  user_id: string;
  author_name: string;
  circle_name: string;
  shared_with: number[];
  share_names: string[];
  store: "crm" | "kanban";
  meta: Record<string, unknown>;
};

type RawTask = {
  id?: string;
  title?: string;
  description?: string;
  status?: string;
  start?: string | null;
  client_name?: string | null;
  user_id?: string | number;
  author_name?: string;
  circle_name?: string;
  shared_with?: number[];
  share_names?: string[];
  store?: string;
  view_id?: string;
  allDay?: number | boolean | string;
  all_day?: number | boolean | string;
  meta?: Record<string, unknown> | string | null;
};

export type CrmWeekBoard = {
  tasks: WeekTask[];
  kanbanShared: WeekTask[];
  team: ShareMate[];
  properties: Array<{ key: string; label?: string; type?: string; values: Array<{ key: string; label?: string; color?: { bg?: string; text?: string } }> }>;
  note_labels: Array<{ key: string; label?: string; color?: { bg?: string; text?: string } }>;
  linked: boolean;
  known: boolean;
  importedCount: number;
  importedAt: string;
  wipedAt: string;
};

export type ShareMate = { id: number; name: string };

let crmBoard: Promise<CrmWeekBoard> | null = null;
let shareMates: ShareMate[] = [];

export function taskShareMates(): ShareMate[] {
  return shareMates;
}

export function shareIdsOf(task: WeekTask): number[] {
  if (task.shared_with.length > 0) return task.shared_with;
  const raw = task.meta.sharedWith;
  if (!Array.isArray(raw)) return [];
  return raw.map((id) => Number(id)).filter((id) => id > 0);
}

export function isTaskOwner(task: WeekTask): boolean {
  const me = currentUserId();
  return !task.user_id || task.user_id === me;
}

export function dropCrmWeek(): void {
  crmBoard = null;
}

/** Tâche miroir créée depuis Agenda (« Partager vers le CRM / Kanban »). */
export function isCrmAgendaMirror(task: WeekTask): boolean {
  const flag = task.meta.agendaMirror;
  if (flag === false || flag === 0 || flag === "0" || flag === "false") return false;
  if (flag === true || flag === 1 || flag === "1" || flag === "true") return true;
  return task.id.startsWith("agc_") || task.id.startsWith("agmir_");
}

export function agendaMirrorAccent(task: WeekTask): string {
  const raw = String(task.meta.agendaCalendarColorHex || "").trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw}`;
  return "#6366f1";
}

export function taskMarquerAgenda(meta: Record<string, unknown>): boolean {
  return meta.marquer_agenda === true || meta.marquer_agenda === 1 || meta.marquer_agenda === "true";
}

/** Place ou retire un événement Agenda lié à une tâche (hors série périodique). */
export async function syncTaskAgendaLink(
  task: WeekTask,
  options: { place: boolean; calendarId?: string; previousDay?: string; seriesId?: string },
): Promise<void> {
  const userId = currentUserId();
  if (!userId) return;
  const taskId = task.id;
  const calendarId = String(options.calendarId || task.meta.agendaCalendarId || task.meta.agenda_calendar_id || "").trim();
  if (!options.place) {
    await clearTaskAgenda(taskId, task.title || "Tâche", calendarId);
    return;
  }
  const placed = agendaPlacement(task);
  const seriesId = String(options.seriesId || task.meta.seriesId || "").trim();
  const previousDay = String(options.previousDay || "").slice(0, 10);
  const res = await fetch("/api_suite/agenda-import-kanban-task.php", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-User-Id": userId },
    body: JSON.stringify({
      task_id: taskId,
      title: task.title.slice(0, 180) || "Tâche",
      all_day: 0,
      start: placed.start,
      end: placed.end,
      ...(calendarId ? { calendar_id: calendarId } : {}),
      ...(seriesId && seriesId !== taskId ? { series_id: seriesId } : {}),
      ...(previousDay ? { previous_ymd: previousDay } : {}),
    }),
  });
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string };
  if (!res.ok || data.success === false) {
    throw new Error(data.message || "Impossible de placer la tâche dans l’agenda.");
  }
}

function taskFollowsAgenda(task: WeekTask): boolean {
  if (isCrmAgendaMirror(task) && String(task.meta.agendaEventId || "").trim()) return true;
  if (taskMarquerAgenda(task.meta)) return true;
  return Boolean(readPeriod(task.meta)?.placeAgenda);
}

function taskInstant(value: string): string {
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::(\d{2}))?/);
  if (!match) return value.trim();
  return `${match[1]} ${match[2]}:${match[3] || "00"}`;
}

/** Le jour est déjà enregistré : aligne l’événement Agenda (durée conservée). */
export async function shiftTaskAgenda(task: WeekTask, nextStart: string): Promise<boolean> {
  if (!taskFollowsAgenda(task)) return true;
  const previous = task.start.trim();
  const next = nextStart.trim();
  if (!previous || taskInstant(previous) === taskInstant(next)) return true;
  const userId = currentUserId();
  if (!userId) return false;
  const res = await fetch("/api_suite/agenda-shift-task.php", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-User-Id": userId },
    body: JSON.stringify({ task_id: task.id, previous_start: previous }),
  });
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; shifted?: boolean; message?: string };
  if (!res.ok || data.success === false) {
    throw new Error(data.message || "L’agenda n’a pas suivi le déplacement.");
  }
  return data.shifted !== false;
}

function columnTasks(tasks: WeekTask[], day: string): WeekTask[] {
  return tasks
    .filter((task) => taskDay(task) === day)
    .sort((a, b) => {
      const done = Number(a.status === "done") - Number(b.status === "done");
      if (done) return done;
      const aOrder = orderRank(a, day);
      const bOrder = orderRank(b, day);
      if (aOrder !== bOrder) {
        if (aOrder === 999999) return 1;
        if (bOrder === 999999) return -1;
        return aOrder - bOrder;
      }
      return taskClock(a).localeCompare(taskClock(b)) || a.title.localeCompare(b.title, "fr");
    });
}

function clampMoveIndex(task: WeekTask, rows: WeekTask[], index: number): number {
  const openCount = rows.filter((row) => row.status !== "done").length;
  if (task.status === "done") return Math.max(openCount, Math.min(index, rows.length));
  return Math.max(0, Math.min(index, openCount));
}

/** Nouvelle disposition après un glisser-déposer. `null` si rien ne change. */
export function layoutAfterMove(tasks: WeekTask[], id: string, day: string, index: number): WeekTask[] | null {
  const task = tasks.find((row) => row.id === id);
  if (!task || !day) return null;
  const from = taskDay(task);
  const source = columnTasks(tasks, from);
  const oldIndex = source.findIndex((row) => row.id === id);
  const sourceRest = source.filter((row) => row.id !== id);
  const destRest = from === day ? sourceRest : columnTasks(tasks, day);
  const clamped = clampMoveIndex(task, destRest, index);
  if (from === day && clamped === oldIndex) return null;
  const time = task.start.match(/\d{4}-\d{2}-\d{2}[ T](\d{2}:\d{2}:\d{2})/)?.[1] || "09:00:00";
  const moved = from === day ? task : { ...task, start: `${day} ${time}` };
  const dest = [...destRest];
  dest.splice(clamped, 0, moved);
  const updates = new Map<string, WeekTask>();
  dest.map((row, position) => withDayOrder(row, day, position)).forEach((row) => updates.set(row.id, row));
  if (from !== day) sourceRest.map((row, position) => withDayOrder(row, from, position)).forEach((row) => updates.set(row.id, row));
  return tasks.map((row) => updates.get(row.id) || row);
}

export async function persistWeekMove(before: WeekTask[], after: WeekTask[]): Promise<void> {
  const byId = new Map(before.map((row) => [row.id, row]));
  let wrote = false;
  for (const row of after) {
    const previous = byId.get(row.id);
    if (!previous) continue;
    const sameOrder = JSON.stringify(previous.meta.orderByDate || {}) === JSON.stringify(row.meta.orderByDate || {});
    if (previous.start === row.start && sameOrder) continue;
    wrote = true;
    await saveWeekTask(previous, { start: row.start, meta: row.meta }, { quiet: true, orderOnly: true });
    if (taskInstant(previous.start) !== taskInstant(row.start)) {
      const shifted = await shiftTaskAgenda(previous, row.start);
      if (!shifted) throw new Error("L’agenda n’a pas suivi le déplacement.");
    }
  }
  if (wrote) notifyWeekChange();
}

/** Coupe le lien miroir Agenda → CRM/Kanban (l’événement reste dans l’agenda). */
export async function detachAgendaMirror(task: WeekTask): Promise<void> {
  const userId = currentUserId();
  const eventId = String(task.meta.agendaEventId || "").trim();
  if (!userId || !eventId) return;
  await fetch("/api_suite/agenda-detach-mirror.php", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-User-Id": userId },
    body: JSON.stringify({ event_id: eventId }),
  }).catch(() => undefined);
}

export function stripAgendaMirrorMeta(meta: Record<string, unknown>): Record<string, unknown> {
  const next = { ...meta };
  next.agendaMirror = false;
  delete next.agendaEventId;
  delete next.agendaCalendarColorHex;
  return next;
}

export function loadCrmWeek(force = false): Promise<CrmWeekBoard> {
  if (!force && crmBoard) return crmBoard;
  const run = fetch(apiUrl("week.php") + currentUserQuery(), { credentials: "include", cache: "no-store", headers: crmHeaders() })
    .then(async (res) => {
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok || data.success === false) throw new Error(String(data.message || "Semaine CRM indisponible."));
      const tasks: WeekTask[] = [];
      const seen = new Set<string>();
      (Array.isArray(data.tasks) ? data.tasks : []).forEach((row) => {
        const task = normalize(row as RawTask);
        if (!task || seen.has(task.id)) return;
        seen.add(task.id);
        tasks.push(task);
      });
      const kanbanShared: WeekTask[] = [];
      const seenKanban = new Set<string>();
      (Array.isArray(data.kanban_shared) ? data.kanban_shared : []).forEach((row) => {
        const task = normalize(row as RawTask);
        if (!task || seenKanban.has(task.id)) return;
        seenKanban.add(task.id);
        kanbanShared.push(task);
      });
      shareMates = Array.isArray(data.team)
        ? data.team.flatMap((row) => {
          const mate = row as { id?: number; name?: string };
          const id = Number(mate.id) || 0;
          return id > 0 ? [{ id, name: String(mate.name || "Collègue") }] : [];
        })
        : [];
      return {
        tasks,
        kanbanShared,
        team: shareMates,
        properties: Array.isArray(data.properties) ? data.properties as CrmWeekBoard["properties"] : [],
        note_labels: Array.isArray(data.note_labels) ? data.note_labels as CrmWeekBoard["note_labels"] : [],
        linked: data.linked !== false,
        known: data.known === true,
        importedCount: Number(data.importedCount) || 0,
        importedAt: String(data.importedAt || ""),
        wipedAt: String(data.wipedAt || ""),
      };
    })
    .catch((error: unknown) => {
      crmBoard = null;
      throw error;
    });
  crmBoard = run;
  return run;
}

function crmHeaders(): HeadersInit {
  const id = currentUserId();
  return { ...(id ? { "X-User-Id": id } : {}), "Content-Type": "application/json" };
}

function padBlocksOf(source: string): { markdown: string; blocks: unknown[] | null } {
  const raw = source.replace(/\r\n/g, "\n");
  const wrapped = raw.match(/<!--BLOCKS_JSON\s*\n([\s\S]*?)\n-->\s*$/);
  if (wrapped) {
    const blocks = parsePad(wrapped[1]);
    if (blocks) return { markdown: raw.slice(0, wrapped.index).replace(/\s+$/, ""), blocks };
  }
  const marker = "<!--BLOCKS_JSON-->";
  const at = raw.lastIndexOf(marker);
  if (at >= 0) {
    const blocks = parsePad(raw.slice(at + marker.length));
    if (blocks) return { markdown: raw.slice(0, at).replace(/\s+$/, ""), blocks };
  }
  return { markdown: raw, blocks: null };
}

function parsePad(raw: string): unknown[] | null {
  try {
    const parsed = JSON.parse(raw.trim()) as unknown;
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

/** Le pare-feu refuse `<!--` dans le JSON. Le bloc-note part en texte, les blocs dans la meta. */
function packPad(description: string, meta: Record<string, unknown>): { description: string; meta: Record<string, unknown> } {
  const { markdown, blocks } = padBlocksOf(description);
  const next = { ...meta };
  if (blocks?.length) next.padBlocks = blocks;
  else delete next.padBlocks;
  return { description: markdown, meta: next };
}

function unpackPad(description: string, meta: Record<string, unknown>): string {
  if (description.includes("<!--BLOCKS_JSON")) return description;
  const blocks = meta.padBlocks;
  if (!Array.isArray(blocks) || blocks.length === 0) return description;
  const text = description.replace(/\s+$/, "");
  return `${text}\n\n<!--BLOCKS_JSON-->\n${JSON.stringify(blocks)}`;
}

function scrubCrmBody(body: Record<string, unknown>): Record<string, unknown> {
  if (typeof body.description !== "string" || !body.description.includes("<!--BLOCKS_JSON")) return body;
  const meta = body.meta && typeof body.meta === "object" ? { ...(body.meta as Record<string, unknown>) } : {};
  const packed = packPad(body.description, meta);
  return { ...body, description: packed.description, meta: packed.meta };
}

async function postCrm(file: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const payload = scrubCrmBody(body);
  const res = await fetch(apiUrl(file) + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: crmHeaders(),
    body: JSON.stringify(payload),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || data.success === false) {
    const detail = String(data.message || data.error || "").trim();
    throw new Error(detail || `Le CRM ne répond pas (${res.status}).`);
  }
  dropCrmWeek();
  return data;
}

export async function setWeekMode(linked: boolean): Promise<void> {
  await postCrm("week-sync.php", { action: linked ? "link" : "unlink" });
  setKanbanOn(linked);
  notifyWeekChange();
}

export async function importWeek(options: { properties: boolean; reminders: boolean }): Promise<{ tasks: number; properties: number; reminders: number }> {
  const data = await postCrm("week-sync.php", { action: "import", properties: options.properties, reminders: options.reminders });
  notifyWeekChange();
  return { tasks: Number(data.tasks) || 0, properties: Number(data.properties) || 0, reminders: Number(data.reminders) || 0 };
}

export async function saveCrmProperties(
  properties: CrmWeekBoard["properties"],
  noteLabels?: CrmWeekBoard["note_labels"],
): Promise<void> {
  await postCrm("week.php", {
    action: "properties",
    properties,
    ...(noteLabels ? { note_labels: noteLabels } : {}),
  });
  notifyWeekChange();
}

export async function wipeKanbanTasks(): Promise<number> {
  const data = await postCrm("week-sync.php", { action: "wipe" });
  notifyWeekChange();
  return Number(data.removed) || 0;
}

let weekTasksCache: WeekTask[] | null = null;

export function peekWeekTasks(): WeekTask[] | null {
  return weekTasksCache;
}

function rememberWeekTasks(tasks: WeekTask[]): WeekTask[] {
  weekTasksCache = tasks;
  return tasks;
}

/** Le bureau aligne ce drapeau en montant la semaine. Le téléphone le fait ici, avant de charger. */
export async function alignWeekMode(): Promise<void> {
  const board = await loadCrmWeek();
  if (board.known && board.linked !== kanbanOn()) setKanbanOn(board.linked);
}

function mergeTasks(lists: WeekTask[][]): WeekTask[] {
  const seen = new Set<string>();
  const tasks: WeekTask[] = [];
  lists.flat().forEach((task) => {
    if (seen.has(task.id)) return;
    seen.add(task.id);
    tasks.push(task);
  });
  return tasks;
}

export function kanbanBoardName(meta: Record<string, unknown>): string {
  const name = meta.crmBoard;
  return typeof name === "string" ? name.trim() : "";
}

export function kanbanOriginProps(meta: Record<string, unknown>): Array<{ label: string; value: string; color: string }> {
  const raw = meta.crmBoardProps;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const row = item as { label?: unknown; value?: unknown; color?: unknown };
    const label = String(row.label || "").trim();
    const value = String(row.value || "").trim();
    if (!label || !value) return [];
    return [{ label, value, color: typeof row.color === "string" && row.color ? row.color : "#64748b" }];
  });
}

export function boardMeta(input: { board?: string; boardProps?: Array<{ label: string; value: string; color: string }> }): Record<string, unknown> {
  const board = input.board?.trim() || "";
  const props = (input.boardProps || []).filter((row) => row.label.trim() && row.value.trim());
  return {
    ...(board ? { crmBoard: board } : {}),
    ...(props.length > 0 ? { crmBoardProps: props } : {}),
  };
}

export function shareCaption(task: WeekTask): string {
  const me = currentUserId();
  if (task.user_id && me && task.user_id !== me) {
    const who = task.author_name ? `Partagée par ${task.author_name}` : "Partagée avec toi";
    return task.circle_name ? `${who} · ${task.circle_name}` : who;
  }
  const ids = shareIdsOf(task);
  if (ids.length === 0) return "";
  const known = new Map(shareMates.map((mate) => [mate.id, mate.name]));
  const names = ids.map((id, index) => task.share_names[index] || known.get(id) || "").filter(Boolean);
  return names.length > 0 ? `Partagée avec ${names.join(", ")}` : "Partagée";
}

export function shareTone(task: WeekTask): "in" | "out" | "" {
  const me = currentUserId();
  if (task.user_id && me && task.user_id !== me) return "in";
  return shareIdsOf(task).length > 0 ? "out" : "";
}

export function shareToneClass(task: WeekTask): string {
  return shareTone(task) === "in" ? "text-violet-700" : "text-sky-700";
}

function writesKanban(task: WeekTask): boolean {
  if (task.store === "kanban") return true;
  if (isCrmAgendaMirror(task)) return false;
  return kanbanOn() && (!task.user_id || task.user_id === currentUserId());
}

let weekTasksFlight: Promise<WeekTask[]> | null = null;

export function loadWeekTasks(): Promise<WeekTask[]> {
  if (weekTasksFlight) return weekTasksFlight;
  const flight = fetchWeekTasks().finally(() => {
    if (weekTasksFlight === flight) weekTasksFlight = null;
  });
  weekTasksFlight = flight;
  return flight;
}

async function fetchWeekTasks(): Promise<WeekTask[]> {
  if (!kanbanOn()) {
    const board = await loadCrmWeek();
    return rememberWeekTasks(mergeTasks([board.tasks, board.kanbanShared]));
  }
  const userId = currentUserId();
  if (!userId) throw new Error("Session introuvable.");
  const ownUrl = `/api_kanban/kanban/get-tasks.php?kanban=${encodeURIComponent(`default-kanban-${userId}`)}&user_id=${encodeURIComponent(userId)}`;
  const [ownRes, sharedRes, crmBoard] = await Promise.all([
    fetch(ownUrl, { credentials: "include", cache: "no-store" }),
    fetch("/api_kanban/kanban/get-tasks-shared.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    }),
    loadCrmWeek(true).catch(() => null),
  ]);
  const own = (await ownRes.json().catch(() => ({}))) as { ok?: boolean; error?: string; tasks?: RawTask[] };
  if (!ownRes.ok || own.ok === false) throw new Error(own.error || "Mes tâches ne répond pas.");
  const shared = (await sharedRes.json().catch(() => ({}))) as { ok?: boolean; tasks?: RawTask[] };
  const rows = [...(own.tasks || []), ...(shared.ok ? shared.tasks || [] : [])];
  const seen = new Set<string>();
  const tasks: WeekTask[] = [];
  rows.forEach((row) => {
    const task = normalize({ ...row, store: "kanban" });
    if (!task || seen.has(task.id)) return;
    seen.add(task.id);
    tasks.push(task);
  });
  // Miroirs Agenda→CRM (stockés dans crm_week_task, invisibles côté Kanban).
  (crmBoard?.tasks || []).forEach((task) => {
    if (seen.has(task.id)) return;
    if (!isCrmAgendaMirror(task) && task.user_id === userId) return;
    seen.add(task.id);
    tasks.push(task);
  });
  (crmBoard?.kanbanShared || []).forEach((task) => {
    if (seen.has(task.id)) return;
    seen.add(task.id);
    tasks.push(task);
  });
  return rememberWeekTasks(tasks);
}

export async function createOwnedTask(input: { title: string; day: string; time?: string; allDay?: boolean; props?: Record<string, string>; fiche?: { id: number; name: string }; description?: string; board?: string; boardProps?: Array<{ label: string; value: string; color: string }> }): Promise<void> {
  const allDay = input.allDay !== false && !input.time;
  const clock = (input.time || "09:00").slice(0, 5);
  await postCrm("week.php", {
    action: "create",
    id: freshTaskId(),
    title: input.title.slice(0, 180) || "Note",
    description: input.description || "",
    status: "todo",
    day: input.day,
    start: allDay ? `${input.day} 09:00:00` : `${input.day} ${clock}:00`,
    allDay: allDay ? 1 : 0,
    client_name: input.fiche?.name || "",
    meta: { allDay: allDay ? 1 : 0, ...(input.props || {}), ...(input.fiche ? { crmFicheId: input.fiche.id } : {}), ...boardMeta(input) },
  });
  notifyWeekChange();
}

export function blankWeekTask(day: string): WeekTask {
  return {
    id: `draft_${Math.random().toString(16).slice(2, 14)}`,
    title: "",
    description: "",
    status: "todo",
    start: `${day} 08:30:00`,
    client_name: "",
    user_id: currentUserId() || "",
    author_name: "",
    circle_name: "",
    shared_with: [],
    share_names: [],
    store: "crm",
    meta: { allDay: 0, daySpan: "day", agendaEnd: "18:00:00" },
  };
}

export function isDraftWeekTask(task: WeekTask): boolean {
  return task.id.startsWith("draft_");
}

export async function createWeekTask(input: {
  title: string;
  day: string;
  id?: string;
  description?: string;
  status?: string;
  start?: string;
  allDay?: number;
  client_name?: string;
  meta?: Record<string, unknown>;
}): Promise<string> {
  const id = input.id && !input.id.startsWith("draft_") ? input.id : freshTaskId();
  const allDay = input.allDay !== undefined ? input.allDay : 1;
  const start = input.start || `${input.day} 09:00:00`;
  const meta = { allDay, ...(input.meta || {}) };
  if (!kanbanOn()) {
    await postCrm("week.php", {
      action: "create",
      id,
      title: input.title.slice(0, 180) || "Tâche",
      description: input.description || "",
      status: input.status || "todo",
      day: input.day,
      start,
      allDay,
      client_name: input.client_name || "",
      meta,
    });
    notifyWeekChange();
    return id;
  }
  const userId = currentUserId();
  if (!userId) throw new Error("Session introuvable.");
  const res = await fetch("/api_kanban/kanban/save-task.php", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id,
      title: input.title.slice(0, 180) || "Tâche",
      description: input.description || "",
      status: input.status || "todo",
      position: 0,
      start,
      allDay,
      client_name: input.client_name || "",
      view_id: `default-kanban-${userId}`,
      user_id: userId,
      meta,
    }),
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (!res.ok || data.ok === false) throw new Error(data.error || "Création impossible.");
  notifyWeekChange();
  return id;
}

export async function saveWeekTask(
  task: WeekTask,
  patch: Partial<WeekTask> & { allDay?: number },
  options?: { quiet?: boolean; orderOnly?: boolean },
): Promise<void> {
  // patch.meta remplace entièrement la meta (sinon les clés retirées comme repeat restent).
  const next = {
    ...task,
    ...patch,
    meta: patch.meta !== undefined ? { ...patch.meta } : { ...task.meta },
  };
  // Un déplacement ne réécrit que la date et l’ordre. Renvoyer la description
  // (souvent une image) fait échouer ou expirer l’enregistrement.
  const orderOnly = options?.orderOnly === true;
  if (!writesKanban(next)) {
    await postCrm("week.php", {
      action: "update",
      id: next.id,
      ...(orderOnly ? {} : {
        title: next.title,
        description: next.description,
        status: next.status,
        client_name: next.client_name,
      }),
      start: next.start,
      meta: next.meta,
      ...(patch.allDay !== undefined ? { allDay: patch.allDay } : {}),
    });
    if (!options?.quiet) notifyWeekChange();
    return;
  }
  const body: Record<string, unknown> = {
    id: next.id,
    user_id: next.user_id || currentUserId(),
    start: next.start,
    meta: next.meta,
  };
  if (!orderOnly) {
    body.title = next.title;
    body.description = next.description;
    body.status = next.status;
    body.client_name = next.client_name;
  }
  if (patch.allDay !== undefined) body.allDay = patch.allDay;
  const res = await fetch("/api_kanban/kanban/update-task.php", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (!res.ok || data.ok === false) throw new Error(data.error || "Enregistrement impossible.");
  if (!options?.quiet) notifyWeekChange();
}

/** Aligne la note de la fiche et la tâche de suivi quand une tâche semaine est close ou rouverte. */
export async function reflectTaskDone(
  task: WeekTask,
  done: boolean,
  fiche: { id: number; coffre_path: string; coffre_owner_id?: number },
): Promise<void> {
  const title = task.title.trim().slice(0, 180);
  if (fiche.coffre_path) {
    await markTaskNoteDone(fiche.coffre_path, fiche.coffre_owner_id || 0, task.id, done).catch(() => undefined);
  }
  if (fiche.id > 0 && title) {
    await saveSuivi(fiche.id, {
      action: "sync",
      note_key: title,
      title,
      kind: "meeting",
      done,
      due_on: taskDay(task),
    }).catch(() => undefined);
  }
}

/** Clôt ou rouvre la tâche semaine liée à une note de fiche. */
export async function setWeekTaskDone(taskId: string, done: boolean): Promise<void> {
  const id = taskId.trim();
  if (!id) return;
  const tasks = peekWeekTasks() ?? await loadWeekTasks();
  const task = tasks.find((row) => row.id === id);
  if (!task || (task.status === "done") === done) return;
  await saveWeekTask(task, {
    status: done ? "done" : "todo",
    meta: { ...task.meta, statut: done ? "done" : "todo", pending: false },
  });
}

export async function deleteWeekTask(task: WeekTask): Promise<void> {
  if (!isTaskOwner(task)) throw new Error("Seul l’auteur peut supprimer cette tâche.");
  await clearTaskAgenda(task.id);
  if (!writesKanban(task)) {
    await postCrm("week.php", { action: "delete", id: task.id });
    notifyWeekChange();
    return;
  }
  const userId = task.user_id || currentUserId();
  const res = await fetch("/api_kanban/kanban/delete-task.php", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: task.id, user_id: userId }),
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (!res.ok || data.ok === false) throw new Error(data.error || "Suppression impossible.");
  notifyWeekChange();
}

const weekWatchers = new Set<() => void>();

export function watchWeek(listener: () => void): () => void {
  weekWatchers.add(listener);
  return () => { weekWatchers.delete(listener); };
}

export function notifyWeekChange(): void {
  weekWatchers.forEach((listener) => listener());
}

let rollPendingLock: Promise<number> | null = null;

/** Comme Kanban : une tâche « en attente » datée dans le passé remonte au jour courant. */
export async function rollPendingTasks(): Promise<number> {
  if (rollPendingLock) return rollPendingLock;
  rollPendingLock = (async () => {
    const today = localDay();
    dropCrmWeek();
    const tasks = await loadWeekTasks();
    const todayOpen = tasks.filter((row) => taskDay(row) === today && row.status !== "done");
    let nextOrder = todayOpen.reduce((max, row) => {
      const rank = orderRank(row, today);
      return rank === 999999 ? max : Math.max(max, rank + 1);
    }, todayOpen.length);
    let moved = 0;
    for (const task of tasks) {
      if (task.status === "done") continue;
      const paused = task.status === "pending" || task.meta.pending === true || task.meta.pending === 1 || task.meta.pending === "1";
      if (!paused) continue;
      const day = taskDay(task);
      if (!day || day >= today) continue;
      const time = clock(task.start) || "09:00:00";
      const ordered = withDayOrder(task, today, nextOrder);
      nextOrder += 1;
      // quiet : sinon chaque save notifie watchWeek → roll → boucle infinie au retour semaine.
      await saveWeekTask(task, { start: `${today} ${time}`, meta: ordered.meta }, { quiet: true });
      moved += 1;
    }
    return moved;
  })().finally(() => {
    rollPendingLock = null;
  });
  return rollPendingLock;
}

export function dueTodayCounts(tasks: WeekTask[], fiches: Array<{ id: number; name: string }>, day = localDay()): Map<number, number> {
  const counts = new Map<number, number>();
  tasks.forEach((task) => {
    if (task.status === "done" || taskDay(task) !== day) return;
    const fiche = linkedFiche(fiches, task);
    if (!fiche) return;
    counts.set(fiche.id, (counts.get(fiche.id) || 0) + 1);
  });
  return counts;
}

function linkedFiche(fiches: Array<{ id: number; name: string }>, task: WeekTask): { id: number; name: string } | undefined {
  const id = Number(task.meta.crmFicheId);
  if (id > 0) {
    const row = fiches.find((fiche) => fiche.id === id);
    if (row) return row;
  }
  const key = task.client_name.trim().toLocaleLowerCase("fr");
  if (!key) return undefined;
  return fiches.find((row) => row.name.trim().toLocaleLowerCase("fr") === key);
}

function localDay(date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function taskDay(task: WeekTask): string {
  return task.start.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] || "";
}

export function orderRank(task: WeekTask, day: string): number {
  const raw = task.meta.orderByDate;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return 999999;
  const value = Number((raw as Record<string, unknown>)[day]);
  return Number.isFinite(value) ? value : 999999;
}

export function withDayOrder(task: WeekTask, day: string, index: number): WeekTask {
  const raw = task.meta.orderByDate;
  const current = raw && typeof raw === "object" && !Array.isArray(raw) ? { ...(raw as Record<string, unknown>) } : {};
  return { ...task, meta: { ...task.meta, orderByDate: { ...current, [day]: index } } };
}

export function taskClock(task: WeekTask): string {
  if (isAllDay(task)) return "";
  const match = task.start.match(/\d{4}-\d{2}-\d{2}[ T](\d{2}:\d{2})/);
  const hhmm = match?.[1] || "";
  if (hhmm === "00:00") return "";
  return hhmm;
}

/** Libellé horloge carte : plage (Matin, Après-midi, Journée) ou HH:mm. */
export function taskTimeLabel(task: WeekTask): string {
  const span = daySpanOf(task);
  if (span === "morning") return "Matin";
  if (span === "afternoon") return "Après-midi";
  if (span === "day") return "Journée";
  const hhmm = taskClock(task);
  return hhmm || (/[ T]00:00(:00)?$/.test(String(task.start || "")) ? "Journée" : "");
}

export function isAllDay(task: WeekTask): boolean {
  const span = String(task.meta.daySpan || "");
  if (span === "time" || span === "morning" || span === "afternoon" || span === "day") return false;
  const value = task.meta.allDay;
  if (value === 1 || value === true || value === "1" || value === "true") return true;
  if (value === 0 || value === false || value === "0" || value === "false") {
    // 00:00 avec allDay off = souvent une journée entière mal flaggée
    return /[ T]00:00(:00)?$/.test(String(task.start || ""));
  }
  return true;
}

export type DaySpan = "time" | "morning" | "afternoon" | "day";

/** Plages qui bloquent Mes RDV une fois posées dans l’agenda lié au type de RDV. */
export const DAY_SPANS: Array<{ id: DaySpan; label: string; start: string; end: string }> = [
  { id: "time", label: "Heure", start: "", end: "" },
  { id: "morning", label: "Matin", start: "08:30", end: "12:30" },
  { id: "afternoon", label: "Après-midi", start: "14:30", end: "18:00" },
  { id: "day", label: "Journée", start: "08:30", end: "18:00" },
];

export function daySpanOf(task: WeekTask): DaySpan {
  const raw = String(task.meta.daySpan || "");
  if (raw === "time" || raw === "morning" || raw === "afternoon" || raw === "day") return raw;
  if (isAllDay(task)) return "day";
  return "time";
}

export function spanBounds(span: DaySpan, time: string): { start: string; end: string } {
  const preset = DAY_SPANS.find((row) => row.id === span);
  if (span !== "time" && preset?.start && preset.end) return { start: preset.start, end: preset.end };
  const start = (time || "09:00").slice(0, 5);
  const end = minutesToHhmm(hhmmToMinutes(start) + 60);
  return { start, end };
}

/** Début et fin horodatés pour l’agenda (jamais une journée « plain », sinon Mes RDV ne la voit pas). */
export function agendaPlacement(task: WeekTask, day = taskDay(task)): { start: string; end: string } {
  const ymd = day || taskDay(task);
  const bounds = spanBounds(daySpanOf(task), taskClock(task) || "09:00");
  const startMin = hhmmToMinutes(bounds.start);
  const endMin = hhmmToMinutes(bounds.end);
  const endDay = endMin <= startMin ? shiftIsoDay(ymd, 1) : ymd;
  return {
    start: `${ymd} ${bounds.start}:00`,
    end: `${endDay} ${bounds.end}:00`,
  };
}

function hhmmToMinutes(hhmm: string): number {
  const [hour, minute] = hhmm.split(":");
  return (Number(hour) || 0) * 60 + (Number(minute) || 0);
}

function minutesToHhmm(total: number): string {
  const wrapped = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const hour = Math.floor(wrapped / 60);
  const minute = wrapped % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function shiftIsoDay(ymd: string, days: number): string {
  const match = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return ymd;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  date.setUTCDate(date.getUTCDate() + days);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function reminderLabel(task: WeekTask): string {
  const value = task.meta.reminder;
  if (value === null || value === undefined || value === "" || value === "null") return "";
  const minutes = Number(value);
  if (!Number.isFinite(minutes)) return "";
  return REMINDERS.find((row) => row.minutes === minutes)?.short || (minutes === 0 ? "À l’heure" : `${minutes} min`);
}

export function reminderChannels(task: WeekTask): { mail: boolean; sms: boolean } {
  if (!reminderLabel(task)) return { mail: false, sms: false };
  return {
    mail: task.meta.notify_mail === true || task.meta.notify_mail === 1 || task.meta.notify_mail === "1" || task.meta.notify_mail === "true",
    sms: task.meta.notify_sms === true || task.meta.notify_sms === 1 || task.meta.notify_sms === "1" || task.meta.notify_sms === "true",
  };
}

export const REMINDERS: Array<{ minutes: number | null; label: string; short: string }> = [
  { minutes: null, label: "Aucun", short: "" },
  { minutes: 0, label: "À l’heure", short: "À l’heure" },
  { minutes: 5, label: "5 minutes avant", short: "5 min" },
  { minutes: 15, label: "15 minutes avant", short: "15 min" },
  { minutes: 30, label: "30 minutes avant", short: "30 min" },
  { minutes: 60, label: "1 heure avant", short: "1 h" },
  { minutes: 120, label: "2 heures avant", short: "2 h" },
  { minutes: 1440, label: "1 jour avant", short: "1 j" },
];

function normalize(row: RawTask): WeekTask | null {
  const id = String(row.id || "");
  const title = String(row.title || "").trim();
  if (!id || !title) return null;
  if (String(row.view_id || "").startsWith("page-kanban-")) return null;
  let meta: Record<string, unknown> = {};
  if (typeof row.meta === "string") {
    try {
      const parsed = JSON.parse(row.meta) as unknown;
      if (parsed && typeof parsed === "object") meta = parsed as Record<string, unknown>;
    } catch {
      meta = {};
    }
  } else if (row.meta && typeof row.meta === "object") {
    meta = row.meta;
  }
  if (meta.allDay === undefined) {
    const top = row.allDay !== undefined ? row.allDay : row.all_day;
    if (top !== undefined) meta = { ...meta, allDay: top };
  }
  const description = String(row.description || "");
  const listed = Array.isArray(row.shared_with) ? row.shared_with.map((id) => Number(id)).filter((id) => id > 0) : [];
  const fromMeta = Array.isArray(meta.sharedWith) ? meta.sharedWith.map((id) => Number(id)).filter((id) => id > 0) : [];
  const sharedWith = listed.length > 0 ? listed : fromMeta;
  meta.sharedWith = sharedWith;
  return {
    id,
    title,
    description: unpackPad(description, meta),
    status: String(row.status || "todo"),
    start: String(row.start || ""),
    client_name: String(row.client_name || ""),
    user_id: String(row.user_id || ""),
    author_name: String(row.author_name || ""),
    circle_name: String(row.circle_name || ""),
    shared_with: sharedWith,
    share_names: Array.isArray(row.share_names) ? row.share_names.map((name) => String(name)) : [],
    store: row.store === "kanban" ? "kanban" : "crm",
    meta,
  };
}

function freshTaskId(): string {
  return `task_${Math.random().toString(16).slice(2, 14)}`;
}

function clock(start: string): string {
  const match = start.match(/\d{4}-\d{2}-\d{2}[ T](\d{2}:\d{2}:\d{2})/);
  return match?.[1] || "";
}

export type PeriodRule = {
  freq: "weekly" | "monthly";
  interval: number;
  endType: "never" | "until" | "count";
  until?: string;
  count?: number;
  startDate?: string;
  occurrenceIndex: number;
  placeAgenda: boolean;
};

export function readPeriod(meta: Record<string, unknown>): PeriodRule | null {
  const raw = meta.repeat;
  if (raw == null || raw === "" || raw === false) return null;
  if (raw === "weekly" || raw === "monthly") {
    return { freq: raw, interval: 1, endType: "never", occurrenceIndex: 0, placeAgenda: false };
  }
  if (typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const freq = row.freq === "monthly" ? "monthly" : row.freq === "weekly" ? "weekly" : null;
  if (!freq) return null;
  const endType = row.endType === "until" || row.endType === "count" ? row.endType : "never";
  const rule: PeriodRule = {
    freq,
    interval: Math.max(1, Math.min(99, Number(row.interval) || 1)),
    endType,
    occurrenceIndex: Math.max(0, Number(row.occurrenceIndex) || 0),
    placeAgenda: row.placeAgenda === true || row.placeAgenda === 1 || row.placeAgenda === "true",
  };
  if (typeof row.startDate === "string") rule.startDate = row.startDate.slice(0, 10);
  if (endType === "until" && typeof row.until === "string") rule.until = row.until.slice(0, 10);
  if (endType === "count") rule.count = Math.max(1, Math.min(999, Number(row.count) || 1));
  return rule;
}

export function periodLabel(rule: PeriodRule | null): string {
  if (!rule) return "";
  if (rule.freq === "weekly") return rule.interval === 1 ? "Hebdo" : `Toutes les ${rule.interval} sem.`;
  return rule.interval === 1 ? "Mensuel" : `Tous les ${rule.interval} mois`;
}

export function periodProgress(meta: Record<string, unknown>, status = ""): string {
  const total = Number(meta.occurrenceTotal) || 0;
  const index = Math.max(0, Number(meta.occurrenceIndex) || 0);
  if (total < 2) return "";
  const after = Math.max(0, total - index - 1);
  const until = String(meta.seriesUntil || "").slice(0, 10);
  const untilDate = parseIso(until);
  const untilText = untilDate
    ? ` · jusqu’au ${untilDate.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}`
    : "";
  const today = parseIso(localDay());
  const days = untilDate && today ? Math.max(0, Math.round((untilDate.getTime() - today.getTime()) / 86400000)) : null;
  const daysText = days != null ? ` · ${days} j` : "";
  if (status === "done") return `Terminée ${index + 1}/${total}${untilText}`;
  if (after === 0) return `${index + 1}/${total} · dernière${untilText}`;
  return `${index + 1}/${total} · ${after} restante${after > 1 ? "s" : ""}${daysText}${untilText}`;
}

export async function syncPeriodAgenda(
  task: WeekTask,
  rule: PeriodRule | null,
  calendarId: string,
  seriesIdOverride?: string,
): Promise<void> {
  const userId = currentUserId();
  if (!userId) return;
  // Toujours l’id de série d’origine (avant delete meta.seriesId à la désactivation).
  const seriesId = String(seriesIdOverride || task.meta.seriesId || task.id);
  dropCrmWeek();
  const siblings = (await loadWeekTasks()).filter((row) => row.id === seriesId || String(row.meta.seriesId || "") === seriesId);
  const linked = siblings.map((row) => row.id);
  const idByDay = new Map<string, string>();
  siblings.forEach((row) => {
    const day = taskDay(row);
    if (day && !idByDay.has(day)) idByDay.set(day, row.id);
  });
  if (!rule?.placeAgenda) {
    await clearTaskAgenda(seriesId, task.title || "Tâche", calendarId, linked);
    return;
  }
  if (!calendarId.trim()) {
    throw new Error("Choisis un agenda pour placer les occurrences.");
  }
  const ymd = rule.startDate || taskDay(task);
  if (!ymd) return;
  const dates = occurrenceDates(rule, ymd);
  const series = dates.map((day) => {
    const placed = agendaPlacement(task, day);
    return {
      occurrence_ymd: day,
      task_id: idByDay.get(day) || "",
      start: placed.start,
      end: placed.end,
      all_day: 0,
    };
  });
  const res = await fetch("/api_suite/agenda-import-kanban-task.php", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-User-Id": userId },
    body: JSON.stringify({
      task_id: seriesId,
      title: task.title,
      all_day: 0,
      replace_series: 1,
      series,
      linked_task_ids: linked,
      calendar_id: calendarId,
    }),
  });
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string; error?: string };
  if (!res.ok || data.success === false) {
    throw new Error(data.message || "Impossible de placer les occurrences dans l’agenda.");
  }
}

/** Pose une tâche CRM par occurrence (comme l’agenda), indépendantes à la validation. */
export async function syncPeriodTasks(task: WeekTask, rule: PeriodRule | null): Promise<void> {
  const seriesId = String(task.meta.seriesId || task.id);
  dropCrmWeek();
  const all = await loadWeekTasks();
  const siblings = all.filter((row) => row.id === seriesId || String(row.meta.seriesId || "") === seriesId);

  if (!rule) {
    for (const row of siblings) {
      if (row.id !== task.id && row.status !== "done") {
        await deleteWeekTaskQuiet(row);
        continue;
      }
      const meta = { ...row.meta };
      delete meta.repeat;
      delete meta.seriesId;
      delete meta.occurrenceIndex;
      delete meta.occurrenceTotal;
      delete meta.seriesUntil;
      delete meta.repeatStartDate;
      delete meta.marquer_agenda;
      await saveWeekTask(row, { meta });
    }
    notifyWeekChange();
    return;
  }

  const start = rule.startDate || taskDay(task);
  if (!start) return;
  const dates = occurrenceDates(rule, start);
  if (dates.length === 0) return;
  const total = dates.length;
  const until = dates[dates.length - 1] || start;
  const span = daySpanOf(task);
  const bounds = spanBounds(span, taskClock(task) || "09:00");
  const baseMeta = seriesBaseMeta(task.meta);

  const slots: Array<{ day: string; row: WeekTask | null }> = dates.map((day) => ({ day, row: null }));
  const pool = [...siblings];

  for (const row of pool) {
    const day = taskDay(row);
    const slot = slots.find((item) => item.day === day && !item.row);
    if (slot) slot.row = row;
  }

  if (!slots.some((item) => item.row?.id === task.id)) {
    const free = slots.find((item) => !item.row) || slots[0];
    free.row = task;
  }

  const kept = new Set<string>();
  for (let index = 0; index < slots.length; index += 1) {
    const { day, row } = slots[index];
    const startAt = `${day} ${bounds.start}:00`;
    const seriesMeta: Record<string, unknown> = {
      ...baseMeta,
      seriesId,
      occurrenceIndex: index,
      occurrenceTotal: total,
      seriesUntil: until,
      repeatStartDate: start,
      repeat: { ...rule, startDate: start, occurrenceIndex: index, placeAgenda: rule.placeAgenda },
      marquer_agenda: Boolean(rule.placeAgenda),
      allDay: 0,
      daySpan: span,
      agendaEnd: `${bounds.end}:00`,
    };
    if (row) {
      kept.add(row.id);
      const meta = {
        ...row.meta,
        ...seriesMeta,
        statut: row.status === "done" ? "done" : seriesMeta.statut ?? row.meta.statut ?? "todo",
        pending: row.status === "pending",
      };
      await saveWeekTask(row, {
        title: task.title,
        description: index === 0 ? task.description : row.description || task.description,
        start: startAt,
        allDay: 0,
        client_name: task.client_name,
        meta,
      });
    } else {
      const id = await createWeekTask({
        title: task.title,
        day,
        description: task.description,
        status: "todo",
        start: startAt,
        allDay: 0,
        client_name: task.client_name,
        meta: { ...seriesMeta, pending: false },
      });
      kept.add(id);
    }
  }

  for (const row of siblings) {
    if (kept.has(row.id)) continue;
    if (row.status === "done") {
      await saveWeekTask(row, {
        meta: {
          ...row.meta,
          seriesId,
          occurrenceTotal: total,
          seriesUntil: until,
          repeatStartDate: start,
          repeat: { ...rule, startDate: start, placeAgenda: rule.placeAgenda },
        },
      });
      continue;
    }
    await deleteWeekTaskQuiet(row);
  }
  notifyWeekChange();
}

function seriesBaseMeta(meta: Record<string, unknown>): Record<string, unknown> {
  const skip = new Set([
    "repeat", "seriesId", "occurrenceIndex", "occurrenceTotal", "seriesUntil",
    "repeatStartDate", "marquer_agenda", "orderByDate",
  ]);
  const out: Record<string, unknown> = {};
  Object.entries(meta).forEach(([key, value]) => {
    if (!skip.has(key)) out[key] = value;
  });
  return out;
}

async function deleteWeekTaskQuiet(task: WeekTask): Promise<void> {
  if (!writesKanban(task)) {
    await postCrm("week.php", { action: "delete", id: task.id });
    return;
  }
  const userId = task.user_id || currentUserId();
  await fetch("/api_kanban/kanban/delete-task.php", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: task.id, user_id: userId }),
  }).catch(() => undefined);
}

async function clearTaskAgenda(taskId: string, title = "Tâche", calendarId = "", linkedIds: string[] = []): Promise<void> {
  const userId = currentUserId();
  if (!userId || !taskId) return;
  const linked = linkedIds.map((id) => id.trim()).filter((id) => id && id.length <= 64);
  // 1) Série vide via import (accepte CRM + purge tous les miroirs kanban_task_id).
  const wipe = await fetch("/api_suite/agenda-import-kanban-task.php", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-User-Id": userId },
    body: JSON.stringify({
      task_id: taskId,
      title: title.slice(0, 180) || "Tâche",
      replace_series: 1,
      series: [],
      ...(linked.length > 0 ? { linked_task_ids: linked } : {}),
      ...(calendarId.trim() ? { calendar_id: calendarId } : {}),
    }),
  });
  const wipeData = (await wipe.json().catch(() => ({}))) as { success?: boolean; message?: string };
  if (wipe.ok && wipeData.success !== false) return;

  // 2) Fallback delete dédié.
  const res = await fetch("/api_suite/agenda-delete-kanban-task-event.php", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-User-Id": userId },
    body: JSON.stringify({ task_id: taskId }),
  });
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string };
  if (!res.ok || data.success === false) {
    throw new Error(wipeData.message || data.message || "Impossible de retirer les occurrences de l’agenda.");
  }
}

export function occurrenceDates(rule: PeriodRule, start: string): string[] {
  const out: string[] = [];
  let cursor = parseIso(start);
  if (!cursor) return out;
  const horizon = new Date(cursor);
  horizon.setDate(horizon.getDate() + 400);
  let index = 0;
  while (out.length < 52) {
    if (rule.endType === "count" && rule.count != null && index >= rule.count) break;
    if (rule.endType === "until" && rule.until) {
      const until = parseIso(rule.until);
      if (until && cursor > until) break;
    }
    if (cursor > horizon) break;
    out.push(formatIso(cursor));
    cursor = rule.freq === "weekly" ? addDate(cursor, 7 * rule.interval) : addMonths(cursor, rule.interval);
    index += 1;
  }
  return out;
}

function parseIso(value: string): Date | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function formatIso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function addDate(date: Date, count: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + count);
  return copy;
}

function addMonths(date: Date, count: number): Date {
  const copy = new Date(date);
  const day = copy.getDate();
  copy.setMonth(copy.getMonth() + count);
  if (copy.getDate() < day) copy.setDate(0);
  return copy;
}
